import { Fragment } from 'react'
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import clsx from 'clsx'

// ── Spinner ─────────────────────────────────────────────────────────────────
export function Spinner({ size = 'md', className = '' }) {
  const sizes = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-10 h-10' }
  return (
    <svg className={clsx(sizes[size], 'animate-spin text-[var(--color-primary)]', className)}
      fill="none" viewBox="0 0 24 24">
      <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  )
}

// ── Loading screen ───────────────────────────────────────────────────────────
export function LoadingScreen() {
  return (
    <div className="fixed inset-0 flex items-center justify-center" style={{ background: '#020818' }}>
      <div className="flex flex-col items-center gap-4">
        <SystezaLogo size={48} />
        <Spinner size="md" />
      </div>
    </div>
  )
}

// ── Systeza Logo SVG ─────────────────────────────────────────────────────────
export function SystezaLogo({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none">
      <defs>
        <radialGradient id="lgBg" cx="35%" cy="30%" r="75%">
          <stop offset="0%" stopColor="#1C5BE8" />
          <stop offset="55%" stopColor="#1040C8" />
          <stop offset="100%" stopColor="#00B8D4" />
        </radialGradient>
      </defs>
      <circle cx="60" cy="60" r="58" fill="url(#lgBg)" />
      <path d="M 22 38 Q 22 28 32 28 L 88 28 Q 98 28 98 38 Q 98 44 88 44 L 32 44 Q 22 44 22 50"
        stroke="white" strokeWidth="5.5" fill="none" strokeLinecap="round" />
      <circle cx="55" cy="28" r="3.5" fill="white" opacity="0.9" />
      <circle cx="80" cy="44" r="3.5" fill="white" opacity="0.9" />
      <path d="M 22 50 Q 22 60 32 60 L 88 60 Q 98 60 98 70 Q 98 80 88 80 L 32 80 Q 22 80 22 86"
        stroke="white" strokeWidth="5.5" fill="none" strokeLinecap="round" />
      <path d="M 20 82 L 88 82 Q 98 82 98 92" stroke="white" strokeWidth="4" fill="none"
        strokeLinecap="round" opacity="0.7" />
      <circle cx="42" cy="88" r="3.5" fill="white" opacity="0.85" />
    </svg>
  )
}

// ── Button ───────────────────────────────────────────────────────────────────
export function Button({ children, variant = 'primary', size = 'md', className = '', loading = false, ...props }) {
  const base = 'inline-flex items-center justify-center gap-2 font-semibold rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer'
  const variants = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    ghost: 'btn-ghost',
    danger: 'btn-danger',
  }
  const sizes = { sm: 'px-3.5 py-1.5 text-xs', md: 'px-5 py-2.5 text-sm', lg: 'px-7 py-3.5 text-sm' }
  return (
    <button className={clsx(base, variants[variant], sizes[size], className)} disabled={loading || props.disabled} {...props}>
      {loading ? <Spinner size="sm" className="mr-1" /> : null}
      {children}
    </button>
  )
}

// ── Input ────────────────────────────────────────────────────────────────────
export function Input({ label, error, hint, className = '', ...props }) {
  return (
    <div>
      {label && <label className="label">{label}</label>}
      <input className={clsx('input', error && 'input-error', className)} {...props} />
      {hint  && <p className="text-xs text-[var(--text-dim)] mt-1.5">{hint}</p>}
      {error && <p className="text-xs text-red-400 mt-1.5">{error}</p>}
    </div>
  )
}

// ── Select ───────────────────────────────────────────────────────────────────
export function Select({ label, error, options = [], className = '', ...props }) {
  return (
    <div>
      {label && <label className="label">{label}</label>}
      <select className={clsx('input cursor-pointer', error && 'input-error', className)} {...props}>
        {options.map(o => (
          <option key={o.value} value={o.value} style={{ background: '#040E2A' }}>{o.label}</option>
        ))}
      </select>
      {error && <p className="text-xs text-red-400 mt-1.5">{error}</p>}
    </div>
  )
}

// ── Textarea ─────────────────────────────────────────────────────────────────
export function Textarea({ label, error, rows = 4, className = '', ...props }) {
  return (
    <div>
      {label && <label className="label">{label}</label>}
      <textarea rows={rows} className={clsx('input resize-none', error && 'input-error', className)} {...props} />
      {error && <p className="text-xs text-red-400 mt-1.5">{error}</p>}
    </div>
  )
}

// ── Card ─────────────────────────────────────────────────────────────────────
export function Card({ children, className = '', hover = false, onClick }) {
  return (
    <div className={clsx(hover ? 'card-hover' : 'card', onClick && 'cursor-pointer', className)} onClick={onClick}>
      {children}
    </div>
  )
}

// ── PageHeader ───────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, children, badge }) {
  return (
    <div className="flex items-start justify-between mb-6">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="page-title">{title}</h1>
          {badge && <span className={clsx('badge', badge.className)}>{badge.label}</span>}
        </div>
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-3 flex-shrink-0">{children}</div>}
    </div>
  )
}

// ── StatCard ─────────────────────────────────────────────────────────────────
export function StatCard({ label, value, icon, color = '#1A6AF5', delta, loading = false }) {
  return (
    <div className="stat-card">
      <div className="stat-icon" style={{ background: `${color}18`, border: `1px solid ${color}30` }}>
        {typeof icon === 'string' ? (
          <span style={{ fontSize: 20 }}>{icon}</span>
        ) : (
          <span style={{ color }}>{icon}</span>
        )}
      </div>
      <div>
        {loading ? (
          <>
            <div className="skeleton h-7 w-16 mb-1" />
            <div className="skeleton h-3 w-20" />
          </>
        ) : (
          <>
            <p className="text-2xl font-bold font-display text-[var(--text-primary)] tabular-nums">{value}</p>
            <p className="text-xs text-[var(--text-dim)] mt-0.5 font-mono">{label}</p>
            {delta !== undefined && (
              <p className={clsx('text-xs mt-0.5', delta >= 0 ? 'text-emerald-400' : 'text-red-400')}>
                {delta >= 0 ? '↑' : '↓'} {Math.abs(delta)}%
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ── DetailLoader ─────────────────────────────────────────────────────────────
// The spinner every :id page shows while its record is in flight. Same markup as
// the Suspense fallback in App.jsx, so a lazy page and its data read as one wait.
export function DetailLoader() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 rounded-full border-2 border-[var(--color-primary)] border-t-transparent animate-spin" />
    </div>
  )
}

// ── FieldGrid ────────────────────────────────────────────────────────────────
// Label-over-value tiles for the summary strip on a detail page.
// `fields` is [{ label, value }]; a null/undefined value renders an em dash.
export function FieldGrid({ fields = [], cols = 3, className = '' }) {
  return (
    <div className={clsx('grid gap-3 mb-5', className)} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
      {fields.map(({ label, value }) => (
        <div key={label} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)' }}>
          <p className="text-[9px] font-mono text-[var(--text-dim)] mb-1">{label.toUpperCase()}</p>
          <div className="text-xs text-[var(--text-primary)] break-words">
            {value === null || value === undefined || value === '' ? <span className="text-[var(--text-dim)]">—</span> : value}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── EmptyState ───────────────────────────────────────────────────────────────
export function EmptyState({ icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center px-6">
      <div className="w-16 h-16 rounded-2xl card flex items-center justify-center mb-5 text-2xl">{icon}</div>
      <p className="font-semibold text-[var(--text-primary)] mb-1.5 font-display">{title}</p>
      <p className="text-sm text-[var(--text-dim)] max-w-xs mb-6">{description}</p>
      {action}
    </div>
  )
}

// ── Skeleton ─────────────────────────────────────────────────────────────────
export function Skeleton({ className = '', lines = 1 }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className={clsx('skeleton', i === lines - 1 && lines > 1 ? 'w-3/4' : 'w-full', 'h-4', className)} />
      ))}
    </div>
  )
}

// ── StatusBadge ──────────────────────────────────────────────────────────────
const statusMap = {
  active:      'badge badge-green',
  online:      'badge badge-green',
  healthy:     'badge badge-green',
  complete:    'badge badge-green',
  completed:   'badge badge-green',
  passed:      'badge badge-green',
  open:        'badge badge-blue',
  pending:     'badge badge-yellow',
  in_progress: 'badge badge-cyan',
  running:     'badge badge-cyan',
  generating:  'badge badge-cyan',
  warning:     'badge badge-yellow',
  failed:      'badge badge-red',
  error:       'badge badge-red',
  closed:      'badge badge-gray',
  resolved:    'badge badge-gray',
  inactive:    'badge badge-gray',
  paused:      'badge badge-gray',
  draft:       'badge badge-gray',
  cancelled:   'badge badge-gray',
  partial:     'badge badge-yellow',
}

export function StatusBadge({ status, label }) {
  const cls = statusMap[status?.toLowerCase()] || 'badge badge-gray'
  return <span className={cls}>{label || status}</span>
}

// ── Alert ─────────────────────────────────────────────────────────────────────
export function Alert({ type = 'error', children, onDismiss }) {
  const styles = {
    error:   'bg-red-500/10 border-red-500/20 text-red-400',
    success: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    warning: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
    info:    'bg-blue-500/10 border-blue-500/20 text-blue-400',
  }
  return (
    <div className={clsx('flex items-start gap-3 rounded-xl border px-4 py-3 text-sm', styles[type])}>
      <span className="mt-0.5 flex-shrink-0">
        {type === 'error' && '✕'}
        {type === 'success' && '✓'}
        {type === 'warning' && '⚠'}
        {type === 'info' && 'ℹ'}
      </span>
      <span className="flex-1">{children}</span>
      {onDismiss && (
        <button onClick={onDismiss} className="ml-2 opacity-60 hover:opacity-100 transition-opacity">✕</button>
      )}
    </div>
  )
}

// ── ConfirmDialog ─────────────────────────────────────────────────────────────
export function ConfirmDialog({ open, onClose, onConfirm, title, description, confirmLabel = 'Confirm', variant = 'danger', loading = false }) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="card max-w-md w-full p-7" style={{ background: '#040E2A', border: '1px solid #1A2F60' }}>
        <h3 className="font-display font-bold text-lg text-[var(--text-primary)] mb-2">{title}</h3>
        {description && <p className="text-sm text-[var(--text-dim)] mb-6">{description}</p>}
        <div className="flex gap-3 justify-end">
          <Button variant="ghost" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant={variant} onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
        </div>
      </div>
    </div>
  )
}

// ── DataTable ─────────────────────────────────────────────────────────────────
export function DataTable({ columns, data = [], loading = false, loadingText = 'Loading...', emptyText = 'No data found.', emptyIcon = '📋', onRowClick }) {
  if (loading) return (
    <div className="card p-0 overflow-hidden">
      <div className="p-10 text-center text-[var(--text-dim)] text-sm font-mono">{loadingText}</div>
    </div>
  )
  if (!data.length) return (
    <EmptyState icon={emptyIcon} title={emptyText} description="" />
  )
  return (
    <div className="card p-0 overflow-hidden table-wrapper">
      <table className={clsx('table', onRowClick && 'table-clickable')}>
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col.key} style={{ textAlign: col.align || 'left' }}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={row.id ?? i} onClick={onRowClick ? () => onRowClick(row) : undefined}>
              {columns.map(col => (
                <td key={col.key} style={{ textAlign: col.align || 'left' }} className={col.className}>
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Pagination ───────────────────────────────────────────────────────────────
export function Pagination({ meta, onPageChange }) {
  if (!meta || meta.last_page <= 1) return null
  const pages = Array.from({ length: meta.last_page }, (_, i) => i + 1)
  return (
    <div className="flex items-center justify-between mt-4">
      <p className="text-xs text-[var(--text-dim)] font-mono">
        Showing {((meta.current_page - 1) * meta.per_page) + 1}–{Math.min(meta.current_page * meta.per_page, meta.total)} of {meta.total}
      </p>
      <div className="flex gap-1">
        {pages.map(p => (
          <button key={p} onClick={() => onPageChange(p)}
            className={clsx('w-8 h-8 rounded-lg text-xs font-mono transition-all',
              meta.current_page === p
                ? 'bg-[var(--color-primary)] text-white'
                : 'text-[var(--text-dim)] hover:bg-white/5 hover:text-[var(--text-muted)]'
            )}>
            {p}
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Modal ────────────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, size = 'md' }) {
  if (!open) return null
  const maxWidths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl', full: 'max-w-6xl' }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <div className={clsx('w-full card', maxWidths[size])}
        style={{ background: '#040E2A', border: '1px solid #1A2F60', animation: 'fadeUp 0.2s ease-out' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-lg text-[var(--text-primary)]">{title}</h3>
          <button onClick={onClose} className="btn-ghost p-1.5 rounded-lg text-[var(--text-dim)] hover:text-white">✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}

// ── Tabs ─────────────────────────────────────────────────────────────────────
export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="flex border-b border-[var(--border-subtle)] mb-6">
      {tabs.map(tab => (
        <button key={tab.id} onClick={() => onChange(tab.id)}
          className={clsx('px-5 py-2.5 text-xs font-mono font-semibold border-b-2 transition-colors -mb-px',
            active === tab.id
              ? 'text-[var(--color-accent)] border-[var(--color-accent)]'
              : 'text-[var(--text-dim)] border-transparent hover:text-[var(--text-muted)]'
          )}>
          {tab.label}
          {tab.count !== undefined && (
            <span className={clsx('ml-2 px-1.5 py-0.5 rounded-full text-[9px]',
              active === tab.id ? 'bg-[var(--color-accent)]/20 text-[var(--color-accent)]' : 'bg-white/8 text-[var(--text-dim)]')}>
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  )
}

// ── Dropdown menu ─────────────────────────────────────────────────────────────
export function DropdownMenu({ trigger, items }) {
  return (
    <Menu as="div" className="relative">
      <MenuButton as={Fragment}>{trigger}</MenuButton>
      <Transition
        enter="transition ease-out duration-100" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
        <MenuItems className="absolute right-0 mt-2 w-52 rounded-xl shadow-lg border py-1 z-50 focus:outline-none"
          style={{ background: '#071640', borderColor: '#1A2F60' }}>
          {items.map((item, i) => item === 'divider' ? (
            <div key={i} className="my-1 h-px" style={{ background: '#1A2F60' }} />
          ) : (
            <MenuItem key={i}>
              {({ active }) => (
                <button onClick={item.onClick}
                  className={clsx('flex items-center gap-2 w-full px-4 py-2 text-sm transition-colors',
                    item.danger ? 'text-red-400' : 'text-[var(--text-muted)]',
                    active && (item.danger ? 'bg-red-500/10' : 'bg-white/5 text-white')
                  )}>
                  {item.icon && <span className="w-4 text-center opacity-70">{item.icon}</span>}
                  {item.label}
                </button>
              )}
            </MenuItem>
          ))}
        </MenuItems>
      </Transition>
    </Menu>
  )
}

// ── Avatar ────────────────────────────────────────────────────────────────────
export function Avatar({ name = '', src, size = 'md', color = '#1A6AF5' }) {
  const sizes = { xs: 'w-6 h-6 text-[10px]', sm: 'w-8 h-8 text-xs', md: 'w-10 h-10 text-sm', lg: 'w-12 h-12 text-base' }
  const initials = name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('')
  if (src) {
    return <img src={src} alt={name} className={clsx(sizes[size], 'rounded-full object-cover ring-1 ring-white/10')}
      onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=${color.slice(1)}&color=fff` }} />
  }
  return (
    <div className={clsx(sizes[size], 'rounded-full flex items-center justify-center font-bold flex-shrink-0')}
      style={{ background: color }}>
      {initials || '?'}
    </div>
  )
}

// ── Search bar ────────────────────────────────────────────────────────────────
export function SearchBar({ value, onChange, placeholder = 'Search...', className = '' }) {
  return (
    <div className={clsx('relative', className)}>
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-dim)]">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </span>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className={clsx('input pl-9 pr-3 py-2 text-sm')} />
    </div>
  )
}
